import { create } from "zustand";
import { getAvatarCircle } from "../axios/user";
import { AvatarCircleData } from "../types/user";

interface AvatarCircleCache {
  [userId: string]: {
    data: AvatarCircleData;
    timestamp: number;
  };
}

interface AvatarCircleStore {
  cache: AvatarCircleCache;
  loadingUsers: Set<string>;
  fetchAvatarCircle: (userId: string) => Promise<AvatarCircleData>;
  getCachedData: (userId: string) => AvatarCircleData | null;
  clearCache: () => void;
}

const CACHE_TTL = 5 * 60 * 1000;

export const useAvatarCircleStore = create<AvatarCircleStore>((set, get) => ({
  cache: {},
  loadingUsers: new Set(),

  getCachedData: (userId: string) => {
    const cached = get().cache[userId];
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > CACHE_TTL) {
      const newCache = { ...get().cache };
      delete newCache[userId];
      set({ cache: newCache });
      return null;
    }

    return cached.data;
  },

  fetchAvatarCircle: async (userId: string) => {
    const cached = get().getCachedData(userId);
    if (cached) {
      return cached;
    }

    const { loadingUsers } = get();
    if (loadingUsers.has(userId)) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          const cachedData = get().getCachedData(userId);
          if (cachedData) {
            clearInterval(checkInterval);
            resolve(cachedData);
          } else if (!get().loadingUsers.has(userId)) {
            clearInterval(checkInterval);
            resolve({});
          }
        }, 50);
      });
    }

    set({
      loadingUsers: new Set([...loadingUsers, userId]),
    });

    try {
      const data = await getAvatarCircle(userId);

      set({
        cache: {
          ...get().cache,
          [userId]: {
            data,
            timestamp: Date.now(),
          },
        },
        loadingUsers: new Set(
          Array.from(get().loadingUsers).filter((id) => id !== userId)
        ),
      });

      return data;
    } catch (error) {
      console.error(`Error fetching avatar circle for user ${userId}:`, error);

      set({
        loadingUsers: new Set(
          Array.from(get().loadingUsers).filter((id) => id !== userId)
        ),
      });

      return {};
    }
  },

  clearCache: () => {
    set({ cache: {} });
  },
}));
